import { Button } from '@components/Button'
import {
	Box,
	Callout,
	Heading,
	IconSolidCheckCircle,
	IconSolidInformationCircle,
	IconSolidXCircle,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { auth } from '@util/auth'
import { copyToClipboard } from '@util/string'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { PRIVATE_GRAPH_URI } from '@/constants'
import { useEnsureProjectAnalyticsMutation } from '@/graph/generated/hooks'
import { useApplicationContext } from '@/routers/AppRouter/context/ApplicationContext'
import { styledHorizontalScrollbar } from '@/style/common.css'

const LOGREPLAY_PUBLIC_ORIGIN = 'https://logreplay.com'

const codeBlockStyle = {
	fontFamily:
		'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
	fontSize: 13,
	lineHeight: 1.55,
	margin: 0,
	whiteSpace: 'pre',
} as const

const buildBrowserSnippet = (
	projectId: string,
) => `<script src="${LOGREPLAY_PUBLIC_ORIGIN}/static/sdk/logreplay-recorder.umd.js"></script>
<script>
  H.init('${projectId}', {
    backendUrl: '${LOGREPLAY_PUBLIC_ORIGIN}/public',
  });
</script>`

type InstallationCheckResponse = {
	installed: boolean
	script_found: boolean
	project_id_found: boolean
	normalized_url: string
	error: string | null
}

type InstallationCheckStatus =
	| { type: 'idle' }
	| { type: 'success'; result: InstallationCheckResponse }
	| { type: 'missing'; result: InstallationCheckResponse }
	| { type: 'error'; message: string }

export const SetupPage = () => {
	const { currentProject } = useApplicationContext()
	const [ensureAnalytics] = useEnsureProjectAnalyticsMutation()
	const [searchParams] = useSearchParams()
	const [installationURL, setInstallationURL] = useState('')
	const [installationStatus, setInstallationStatus] =
		useState<InstallationCheckStatus>({ type: 'idle' })
	const [checkingInstallation, setCheckingInstallation] = useState(false)
	const projectId = currentProject?.verbose_id ?? currentProject?.id ?? ''
	const snippet = buildBrowserSnippet(projectId)
	const showWelcome = searchParams.get('welcome') === '1'

	useEffect(() => {
		if (currentProject?.id) {
			ensureAnalytics({
				variables: { project_id: currentProject.id },
			}).catch(() => {})
		}
	}, [currentProject?.id, ensureAnalytics])

	const checkInstallation = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (!currentProject?.id || checkingInstallation) {
			return
		}

		setCheckingInstallation(true)
		setInstallationStatus({ type: 'idle' })

		try {
			const token = await auth.currentUser?.getIdToken()
			const response = await fetch(
				`${PRIVATE_GRAPH_URI.replace(/\/$/, '')}/setup/check-installation`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						token: token ?? '',
					},
					body: JSON.stringify({
						project_id: Number(currentProject.id),
						url: installationURL,
					}),
				},
			)
			const result = (await response.json()) as InstallationCheckResponse

			if (!response.ok) {
				setInstallationStatus({
					type: 'error',
					message:
						result.error ??
						'Could not check that URL. Please try again.',
				})
				return
			}

			setInstallationStatus({
				type: result.installed ? 'success' : 'missing',
				result,
			})
		} catch {
			setInstallationStatus({
				type: 'error',
				message: 'Could not check that URL. Please try again.',
			})
		} finally {
			setCheckingInstallation(false)
		}
	}

	const updateInstallationURL = (value: string) => {
		setInstallationURL(value)
		if (installationStatus.type !== 'idle') {
			setInstallationStatus({ type: 'idle' })
		}
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			height="full"
			overflowY="auto"
			background="default"
		>
			<Box
				display="flex"
				flexDirection="column"
				gap="20"
				p="32"
				style={{ maxWidth: 960, width: '100%', margin: '0 auto' }}
			>
				{showWelcome && (
					<Stack direction="column" gap="8">
						<Heading level="h3">Welcome to LogReplay</Heading>
						<Text color="moderate">
							Your website is ready here. To start seeing
							sessions, logs, and replay data, add the JavaScript
							snippet below to your website.
						</Text>
					</Stack>
				)}

				<Stack direction="column" gap="8">
					<Heading level={showWelcome ? 'h4' : 'h3'}>
						Install the JavaScript snippet
					</Heading>
					<Text color="moderate">
						Add this code to the pages you want to record. Most
						websites only need this snippet added once,{' '}
						<Box
							as="span"
							style={{
								fontWeight: 700,
								borderBottom: '2px dashed currentColor',
								paddingBottom: 2,
							}}
						>
							before the closing head tag
						</Box>{' '}
						or before the app bundle loads.
					</Text>
				</Stack>

				<Callout icon={IconSolidCheckCircle} title="HTML / JavaScript">
					<Text color="moderate">
						This is the only setup path enabled for this deployment
						right now.
					</Text>
				</Callout>

				<Box
					border="secondary"
					borderRadius="8"
					background="raised"
					overflow="hidden"
				>
					<Box
						display="flex"
						alignItems="center"
						borderBottom="secondary"
						px="12"
						py="8"
					>
						<Text weight="bold">JavaScript snippet</Text>
						<Box ml="auto">
							<Button
								trackingId="setup-copy-browser-js-snippet"
								size="small"
								onClick={() =>
									copyToClipboard(snippet, {
										onCopyText:
											'Copied JavaScript snippet to clipboard.',
									})
								}
							>
								Copy
							</Button>
						</Box>
					</Box>
					<Box
						background="nested"
						color="strong"
						overflowX="auto"
						p="16"
						cssClass={styledHorizontalScrollbar}
					>
						<pre style={codeBlockStyle}>{snippet}</pre>
					</Box>
				</Box>

				<Box
					border="secondary"
					borderRadius="8"
					background="raised"
					overflow="hidden"
				>
					<Box borderBottom="secondary" px="12" py="8">
						<Text weight="bold">Check installation</Text>
					</Box>
					<Box p="16">
						<Stack direction="column" gap="12">
							<Text color="moderate">
								Enter a page URL to check whether the returned
								HTML contains the LogReplay script and this
								project ID.
							</Text>
							<Box
								as="form"
								display="flex"
								gap="8"
								style={{ alignItems: 'stretch' }}
								onSubmit={checkInstallation}
							>
								<input
									type="url"
									value={installationURL}
									onChange={(event) =>
										updateInstallationURL(
											event.target.value,
										)
									}
									placeholder="https://example.com"
									required
									style={{
										flex: 1,
										minWidth: 0,
										border: '1px solid var(--color-gray-300)',
										borderRadius: 6,
										padding: '8px 10px',
										fontSize: 14,
									}}
								/>
								<Button
									trackingId="setup-check-browser-js-snippet"
									type="submit"
									size="small"
									loading={checkingInstallation}
									disabled={
										checkingInstallation ||
										!installationURL.trim() ||
										!currentProject?.id
									}
								>
									Check installation
								</Button>
							</Box>
							<InstallationCheckResult
								status={installationStatus}
							/>
						</Stack>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

const InstallationCheckResult = ({
	status,
}: {
	status: InstallationCheckStatus
}) => {
	switch (status.type) {
		case 'success':
			return (
				<Callout
					icon={IconSolidCheckCircle}
					title="LogReplay is installed"
				>
					<Text color="moderate">
						Found the recorder script and this project ID on{' '}
						{status.result.normalized_url}.
					</Text>
				</Callout>
			)
		case 'missing':
			return (
				<Callout
					icon={IconSolidInformationCircle}
					title="Not found yet"
				>
					<Text color="moderate">
						{status.result.error ??
							'The returned HTML did not include the complete LogReplay snippet.'}
					</Text>
				</Callout>
			)
		case 'error':
			return (
				<Callout icon={IconSolidXCircle} title="Could not check URL">
					<Text color="moderate">{status.message}</Text>
				</Callout>
			)
		default:
			return null
	}
}
